<template>
  <div class="mt-4">
    <div class="row tile-row">
      <div
          v-for="item in items"
          :key="`${currentPage}-${item.sku}`"
          class="col-lg-3 col-md-4 col-6"
      >
        <div class="card mb-4 position-relative card-link shadow-sm border-0 rounded-3">
          <div class="image-wrapper">
            <img
                :src="generateImage(item)"
                class="card-img-top"
                :alt="'Product Image of '+item.name"
                loading="lazy"
                decoding="async"
                :class="{ 'image-loaded': imageStates[item.sku] }"
                @load="onImageLoad(item.sku)"
                @error="onImageError"
            >
            <div
                v-if="!imageStates[item.sku]"
                class="image-placeholder"
            >
              <div class="placeholder-shimmer"></div>
            </div>
            <span v-if="isSoldOut(item)" class="badge bg-secondary text-white sold-out-badge">Sold out</span>
          </div>
          <div class="card-footer">
            <router-link class="stretched-link text-reset tile-name" :to="generateLink(item)">{{ item.name }}</router-link>
            <div class="tile-price" data-testid="tile-price">{{ priceLines(item).main }}</div>
            <div class="tile-unit" data-testid="tile-unit">{{ priceLines(item).unit }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, reactive, watch, computed} from 'vue'
import {useRoute} from 'vue-router'

const props = defineProps({
  products: {
    type: Array,
    default: () => []
  },
  isHome: {
    type: Boolean,
    default: false
  }
})

const route = useRoute()
const items = ref(null)
const currentPage = computed(() => route.params.groupId)
const imageStates = reactive({})

const generateLink = (item) => {
  if (props.isHome) {
    return `/products/${item.group}/${item.sku}`
  } else {
    return `/products/${currentPage.value}/${item.sku}`
  }
}

const generateImage = (item) => {
  return `https://larkhallbutchers.co.uk/cdn-cgi/image/width=640,quality=85,format=auto/${item.productImage}`
}

const gbp = new Intl.NumberFormat('en-GB', {style: 'currency', currency: 'GBP'})

// `price` and `fromPrice` are in pence. `fromPrice` (WP-39, L-81) is what the cheapest option the
// customer can choose costs on the product page (its "Cost" for that option), or null when the
// product has no such option. With it:
//   each: "from £81.00"                      (the whole turkey: 4.5kg band x £18, not "from £18.00")
//   kg:   "from £35.20" over "£22.00 per kg" (the smallest option's estimated cost, then the rate)
// Without it (no options, or an API older than WP-39), the line is as before: "from £X" for an
// "each" product, "£X per kg" for a weighed one. The second line is always there, empty when
// unused, so every tile in a row has the same height.
const priceLines = (item) => {
  const pence = Number(item.price)
  if (!Number.isFinite(pence) || pence <= 0) return {main: '', unit: ''}
  const amount = gbp.format(pence / 100)
  const each = !item.weightUnit || item.weightUnit === 'each'
  const from = item.fromPrice
  if (typeof from === 'number' && Number.isFinite(from) && from >= 0) {
    return {main: `from ${gbp.format(from / 100)}`, unit: each ? '' : `${amount} per ${item.weightUnit}`}
  }
  if (each) return {main: `from ${amount}`, unit: ''}
  return {main: `${amount} per ${item.weightUnit}`, unit: ''}
}

// `qty` is the stock limit (-1 = no limit). Zero means nothing can be ordered. What is left of a
// positive limit depends on paid orders, which the list does not carry.
const isSoldOut = (item) => Number(item.qty) === 0

const onImageLoad = (groupId) => {
  imageStates[groupId] = true
}

const onImageError = (event) => {
  event.target.src = '/LHB-LogoType.svg'
}

// Image states are keyed by sku and are not reset when the list changes: a tile that stays on the
// page keeps its loaded image (no second load event would arrive), and a new tile fires its own.
watch(() => props.products, (newProducts) => {
  items.value = newProducts
}, {immediate: true})
</script>

<style scoped>
.image-wrapper {
  position: relative;
  aspect-ratio: 4 / 3;
  min-height: 180px;
  background-color: #f8f9fa;
  overflow: hidden;
  border-radius: 0.75rem 0.75rem 0 0;
}

.card-img-top {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.card-img-top.image-loaded {
  opacity: 1;
}

.image-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
}

.placeholder-shimmer {
  width: 60%;
  height: 60%;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.col-lg-3 {
  transition: transform 0.2s ease;
}
</style>

<style scoped>
.card.card-link {
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.card.card-link:hover {
  transform: translateY(-2px);
  box-shadow: 0 .5rem 1rem rgba(0, 0, 0, 0.15);
}

/* Ensure product titles clamp to two lines and vertically center within reserved space */
.card-footer {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.25rem;
  min-height: calc(1.25em * 3 + 1.25rem);
}
.tile-price {
  font-weight: 600;
  line-height: 1.25;
}
/* The rate under a weighed product's "from" price; reserved (empty) on the other tiles. */
.tile-unit {
  margin-top: -0.25rem;
  min-height: 1.2em;
  font-size: 0.8125rem;
  line-height: 1.2;
  color: var(--tblr-secondary, #667382);
}
.sold-out-badge {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
}
@media (max-width: 575.98px) {
  .tile-row {
    --tblr-gutter-x: 0.75rem;
  }
  .tile-row .card {
    margin-bottom: 0.75rem !important;
  }
  .image-wrapper {
    min-height: 0;
  }
  .card-footer {
    padding: 0.5rem 0.625rem;
    font-size: 0.875rem;
  }
}
.card-footer .stretched-link {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.25;
  word-break: break-word;
  /* Reserve two lines so every tile in a row is the same height. */
  min-height: 2.5em;
}
</style>